import React, { ReactNode, useEffect, useLayoutEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { CircularProgress, CssBaseline, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useOidc, useOidcAccessToken, useOidcIdToken } from '@axa-fr/react-oidc'
import { acceptInvitation } from '../../apis/admin'
import OpenIdStatusPage from '../LandingPage/OpenIdStatusPage'
import localStorage from '../../helpers/localStore'
import {
  INVITATION,
  INVITATION_IV,
  INVITATION_VERSION
} from '../../helpers/search-params.const'
import { setAccessTokenGetter } from '../../actions/tokenBridge'

type Props = {
  children?: ReactNode
}

export const INVITATION_TOKEN = 'invitationToken'

const getQueryParams = () => {
  let urlParams
  if (window.location.search.includes('invitationVersion%3D1')) {
    urlParams = new URLSearchParams(decodeURIComponent(window.location.search))
  } else {
    urlParams = new URLSearchParams(window.location.search)
  }
  return new URLSearchParams(urlParams)
}

const StyledWaitingBox = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100vw',
  height: '100vh'
})

const StyledWaitingText = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(2)
}))

enum CurrentRenderStateEnum {
  normal,
  acceptingInvitation,
  invitationFailed,
  signInFailed
}

const InvitationProvider = ({ children }: Props) => {
  const intl = useIntl()
  const { login } = useOidc()
  const { idTokenPayload } = useOidcIdToken()
  const { accessToken } = useOidcAccessToken()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const params = getQueryParams()
  const invitationVersion = params.get(INVITATION_VERSION)
  const invitationIv = params.get(INVITATION_IV)
  const invitation = params.get(INVITATION)

  const [currentRenderState, setCurrentRenderState] = useState<
    CurrentRenderStateEnum
  >(CurrentRenderStateEnum.normal)

  useLayoutEffect(() => {
    if (invitationVersion && invitationIv && invitation) {
      localStorage.set(INVITATION_TOKEN, {
        invitationVersion,
        invitationIv,
        invitation
      })
      navigate(pathname, { replace: true })
    }
  }, [])

  useEffect(() => {
    setAccessTokenGetter(() => accessToken ?? null)
  }, [accessToken])

  useEffect(() => {
    if (!idTokenPayload || !accessToken) return

    const invitationStored = localStorage.get(INVITATION_TOKEN)
    if (!invitationStored?.invitation) return

    const { invitationVersion, invitationIv, invitation } = invitationStored

    if (!invitationVersion || !invitationIv || !invitation) return

    setCurrentRenderState(CurrentRenderStateEnum.acceptingInvitation)
    localStorage.set(INVITATION_TOKEN, null)

    const acceptThenSignIn = async () => {
      let res: Awaited<ReturnType<typeof acceptInvitation>>

      try {
        res = await acceptInvitation(
          invitationVersion,
          invitationIv,
          invitation,
          {
            Authorization: `Bearer ${accessToken}`
          }
        )
      } catch (err) {
        console.error('acceptInvitation failed', err)

        // Surface the failure instead of retrying login() - should avoid infinite loop.
        // TODO: report `err` to Application Insights - the user-facing copy is
        // deliberately generic, so the raw message is only in the console today.
        setCurrentRenderState(CurrentRenderStateEnum.invitationFailed)

        return
      }

      try {
        if (!res.tenantId) {
          await login(pathname, { noTenant: 'true' })
          return
        }

        const extras: Record<string, string> = {
          tenantId: String(res.tenantId),
          noTenant: 'false'
        }

        if (res.isUserAlreadyMember) {
          await login('/', { ...extras, prompt: 'none' })
        } else {
          await login(pathname, extras)
        }
      } catch (err) {
        console.error('sign-in after accepting invitation failed', err)
        // TODO: report `err` to Application Insights (see above).
        setCurrentRenderState(CurrentRenderStateEnum.signInFailed)
      }
    }

    acceptThenSignIn()
  }, [idTokenPayload, accessToken, login, pathname])

  if (currentRenderState === CurrentRenderStateEnum.acceptingInvitation) {
    return (
      <>
        <CssBaseline />
        <StyledWaitingBox>
          <CircularProgress />
          <Typography variant="h5">
            <StyledWaitingText>
              {intl.formatMessage({ id: 'screens.invitationScreen.waiting' })}
            </StyledWaitingText>
          </Typography>
        </StyledWaitingBox>
      </>
    )
  }

  if (currentRenderState === CurrentRenderStateEnum.invitationFailed) {
    return (
      <OpenIdStatusPage
        headline={intl.formatMessage({
          id: 'screens.invitationScreen.error.headline'
        })}
        body={intl.formatMessage({
          id: 'screens.invitationScreen.error.body'
        })}
        showRetryButton
      />
    )
  }

  if (currentRenderState === CurrentRenderStateEnum.signInFailed) {
    return (
      <OpenIdStatusPage
        headline={intl.formatMessage({
          id: 'screens.invitationScreen.signInError.headline'
        })}
        body={intl.formatMessage({
          id: 'screens.invitationScreen.signInError.body'
        })}
        showRetryButton
      />
    )
  }

  return <>{children}</>
}

export default InvitationProvider
